import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CATEGORIES, CAT_MAP } from "@/lib/categories";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { TrendChart, TrendDelta } from "@/components/ValueTrend";
import { TrendingUp, Info } from "lucide-react";
import { Seo, breadcrumbLd } from "@/components/Seo";

export default function ValuesPage() {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState("tutte");

  useEffect(() => {
    api.get("/value-cards").then((r) => setCards(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = active === "tutte" ? cards : cards.filter((c) => c.category === active);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <Seo
        title="Schede Valore: quanto valgono gli oggetti da collezione"
        description="Fasce di prezzo indicative degli oggetti da collezione più iconici, con i fattori che incidono sul valore, l'andamento delle stime e consigli pratici su cosa comprare e quando."
        path="/valori"
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "Schede Valore", path: "/valori" }])}
      />
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp size={16} className="text-gold" />
        <span className="font-meta text-[10px] uppercase tracking-[0.24em] text-stone-500">Valori Indicativi</span>
      </div>
      <h1 className="font-editorial font-extrabold text-4xl sm:text-5xl tracking-tight text-stone-900">Schede <span className="mark-hl">Valore</span></h1>
      <p className="text-stone-600 mt-4 max-w-2xl">
        Fasce di prezzo indicative degli oggetti da collezione più iconici, con i fattori che incidono sul valore e consigli pratici su cosa comprare, quando e perché.
      </p>
      <div className="mt-5 rounded-2xl border-2 border-dashed border-line bg-panel p-4 max-w-2xl" data-testid="values-method">
        <p className="font-meta text-[9px] uppercase tracking-[0.2em] text-gold mb-1.5">Come nascono questi numeri</p>
        <p className="text-sm text-stone-600 leading-relaxed">
          Dove l'oggetto è identificabile con precisione (marca, modello, numero di riferimento) la fascia è
          calcolata sui <strong className="text-stone-800">prezzi richiesti dalle inserzioni in vendita ora su
          eBay</strong>: prendiamo la mediana e la fascia centrale, scartando gli estremi, e la aggiorniamo ogni
          ora. Sono prezzi <strong className="text-stone-800">richiesti</strong>, non aggiudicazioni. Sulle schede
          segnate come <em>stima AI</em> l'oggetto è troppo variabile perché una ricerca lo identifichi (tirature,
          autentiche, stati di conservazione): lì il numero è una nostra stima, non una quotazione di mercato.
        </p>
      </div>

      <div className="mt-6"><DisclaimerBanner /></div>

      <div className="flex flex-wrap gap-2 mt-8">
        <FilterChip label="Tutte" active={active === "tutte"} onClick={() => setActive("tutte")} />
        {CATEGORIES.map((c) => (
          <FilterChip key={c.slug} label={c.short} hex={c.hex} active={active === c.slug} onClick={() => setActive(c.slug)} testid={`value-filter-${c.slug}`} />
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8 animate-pulse">
          {[...Array(4)].map((_, i) => <div key={i} className="h-64 rounded-xl bg-panel" />)}
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-stone-500 py-16 text-center">Nessuna scheda valore in questa categoria per ora.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          {filtered.map((c, i) => <ValueCard key={c.id} card={c} index={i} />)}
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, active, onClick, hex, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} style={{ "--cat": hex || "#1C1917" }}
      className={`nav-pill ${hex ? "nav-pill-cat" : ""} ${active ? (hex ? "is-active" : "nav-pill-dark") : ""}`}>
      {hex && <span className="dot" />}{label}
    </button>
  );
}

export function ValueCard({ card, index = 0 }) {
  const cat = CAT_MAP[card.category];
  return (
    <div data-testid={`value-card-${index}`} className={`rounded-3xl bg-panel border-2 border-line lift border-l-[6px] ${cat?.accent} p-6`}>
      <div className="flex items-center justify-between gap-3">
        <Link to={`/categoria/${card.category}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[10px] font-meta font-semibold uppercase tracking-[0.12em] border rounded-full ${cat?.badge}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${cat?.dot}`} />{cat?.name}
        </Link>
        {card.era && <span className="text-[11px] font-meta text-stone-500">{card.era}</span>}
      </div>
      <h3 className="font-editorial font-bold text-xl text-stone-900 mt-3.5">
        {card.slug ? (
          <Link to={`/valori/${card.slug}`} data-testid={`value-card-link-${index}`} className="hover:text-gold-soft transition-colors">
            {card.name}
          </Link>
        ) : card.name}
      </h3>

      <div className="mt-4 rounded-xl bg-ink border-2 border-line px-4 py-3 flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5 text-[9px] font-meta uppercase tracking-[0.18em] text-stone-500 mb-1">
            <TrendingUp size={11} className="text-gold" />
            {card.value_source === "ebay-live"
              ? `Prezzi eBay di oggi · ${card.live?.count || 0} inserzioni`
              : "Stima AI indicativa"}
          </div>
          <div className="font-meta font-bold text-gold">{card.value_range}</div>
        </div>
        {card.history?.length >= 2 && <TrendDelta history={card.history} />}
      </div>

      {card.history?.length >= 1 && (
        <div className="mt-4" data-testid={`value-chart-${index}`}>
          <p className="text-[9px] font-meta uppercase tracking-[0.18em] text-stone-500 mb-1">
            {card.value_source === "ebay-live" ? "Andamento dei prezzi (€)" : "Andamento della nostra stima (€)"}
          </p>
          <TrendChart history={card.history} hex={cat?.hex || "#E5C158"} id={`vc-${card.id}`} height={110} />
          {card.history.length >= 2 && (
            <p className="text-[10px] text-stone-400 mt-1">
              {card.value_source === "ebay-live"
                ? "Un punto per rilevamento: mediana dei prezzi richiesti su eBay, non aggiudicazioni."
                : "Punti registrati da noi a ogni revisione della stima AI, non quotazioni di mercato."}
            </p>
          )}
        </div>
      )}

      {card.factors?.length > 0 && (
        <div className="mt-5">
          <p className="text-[9px] font-meta uppercase tracking-[0.18em] text-stone-500 mb-2">Cosa incide sul valore</p>
          <ul className="space-y-1.5">
            {card.factors.map((f, i) => (
              <li key={i} className="text-sm text-stone-600 flex gap-2"><span style={{ color: cat?.hex }}>—</span>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {card.advice && (
        <div className="mt-5 text-sm text-stone-700 rounded-xl bg-ink border-2 border-line p-3.5">
          <span className="font-semibold text-gold-soft">Consiglio pratico: </span>{card.advice}
        </div>
      )}

      {card.note && (
        <p className="mt-3 text-[11px] text-stone-400 flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" />{card.note}</p>
      )}

      {card.slug && (
        <Link to={`/valori/${card.slug}`} data-testid={`value-card-cta-${index}`}
          className="inline-flex items-center gap-1.5 mt-5 text-sm font-semibold text-stone-900 hover:gap-2.5 transition-all">
          Quanto vale: scheda completa <TrendingUp size={14} className="text-gold" />
        </Link>
      )}
    </div>
  );
}
